import { ShipmentCostStore } from 'modules/shipment-cost/stores';
import { BaseViewStore } from 'core/stores';
import { action, makeObservable, _getAdministration, observable } from 'mobx';
import { ApplicationStore } from 'platform/application/stores';
import { BaasicDropdownViewStore } from 'common/stores';
import { LookupStore } from 'modules/lookup/stores';
import moment from 'moment';
import _ from 'lodash';
import { CarrierStore } from 'modules/carrier/stores';
import { ShipmentCostForm } from 'modules/shipment-cost/forms';
import excelJS from 'exceljs';
import XLSX from 'xlsx';
import {
	formatDate,
	translate,
	validateNumberField,
	validateDecimalLengthField,
	validateMinValue,
	excelDateToJSDate,
	isSuperAdminOrganization,
	urlToFile,
} from 'core/utils';
import { ExportDataMapper, localStorageHandler } from 'core/services';
import { ModalParams } from 'core/models';
import { lookupRouteName, sourceOfChange } from 'common/constants';

class ShipmentCostViewStore extends BaseViewStore {
	uploadData = [];
	shipmentInfoIdsFilter = null;
	@observable trackingNumbersResult = {};
	@observable chargeTypeFilter = null;
	@observable isExportDisabled = false;
	@observable shipmentCostTotals = {
		totalBilled: 0.0,
		totalQuoted: 0.0,
		totalOvercharged: 0.0,
		totalUndercharged: 0.0,
		totalDifference: 0.0,
		missingTrackingNumbers: [],
		shipmentInfoIds: [],
	};
	form = new ShipmentCostForm({
		onSuccess: async () => {
			await this.searchShipmentCostAndTotals();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		this.rootStore = rootStore;
		makeObservable(this);
		this.exportDataMapper = new ExportDataMapper();
		this.shipmentCostStore = new ShipmentCostStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.carrierDropdownStore = new BaasicDropdownViewStore(null, false);

		moment.locale(window.navigator.userLanguage || window.navigator.language);
		this.datePickerFormat = moment.localeData().longDateFormat('L');
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);

		this.initialize();
	}

	@action.bound
	initialize = async () => {
		await this.carrierDropdownStore.initialize({
			field: this.form.$('carrier'),
			store: new CarrierStore(this.rootStore),
			filterProperties: {
				IsThirdParty: false,
				page: 1,
				rpp: 50,
			},
		});

		if (this.isSuperAdmin) {
			await this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}

		const filter = localStorageHandler.get('shipment-cost-filter');
		if (filter != null) {
			await this.setCarrierValueOrClear(filter.carrierId);
			if (filter.dateStart) {
				this.form.$('dateRangeStart').value = formatDate(filter.dateStart, true, this.datePickerFormat);
			}
			if (filter.dateEnd) {
				this.form.$('dateRangeEnd').value = formatDate(filter.dateEnd, true, this.datePickerFormat);
			}
			await this.validateFormAndFind(false);

			if (this.isFormValid) {
				await this.searchShipmentCostAndTotals();
			}
		}
	};

	@action.bound reloadSellerCallback = async seller => {
		await this.carrierDropdownStore.updateFilter({
			sellerIds: seller && seller.value ? [seller.value] : null,
			IsThirdParty: false,
		});
		await this.setCarrierValueOrClear(this.carrierDropdownStore.value ? this.carrierDropdownStore.value.value : null);
	};

	@action.bound
	searchShipmentCostAndTotals = async () => {
		this.form.$('searchTrackingNumber').value = '';
		await this.reloadChildViewStores();
	};

	@action.bound
	reloadChildViewStores = async () => {
		let tasks = [];
		const shipmentCostListViewStore = this.rootStore.shipmentCostViewStore.shipmentCostListViewStore;
		if (shipmentCostListViewStore != null) {
			tasks.push(shipmentCostListViewStore.findShipmentCostTn());
		}
		tasks.push(this.reloadChargeList());
		await Promise.all(tasks);
	};

	@action.bound
	reloadChargeList = async () => {
		// Reload charges only if searchTrackingNumber is empty
		if (this.form.$('searchTrackingNumber').value == null || this.form.$('searchTrackingNumber').value === '') {
			const chargeListViewStore = this.rootStore.shipmentCostViewStore.shipmentCostChargeListViewStore;
			if (chargeListViewStore != null) {
				await chargeListViewStore.findShipmentCostCharge();
			}
		}
	};

	async setCarrierValueOrClear(carrierId) {
		if (
			carrierId != null &&
			_.some(this.carrierDropdownStore.options, option => {
				return option.value == carrierId;
			})
		) {
			this.carrierDropdownStore.setValue({ value: carrierId });
			await this.carrierDropdownStore.reload();
		} else {
			this.carrierDropdownStore.clear();
		}
	}

	@action.bound
	async validateFormAndFind(reload = true) {
		await this.form.validate().then(({ isValid }) => {
			if (!isValid) {
				this.isFormValid = isValid;
				this.form.showErrors();
			} else {
				this.isFormValid = isValid;
			}
		});

		if (this.isFormValid && reload) {
			await this.reloadChildViewStores();
		}
	}

	@action.bound
	onTrackingNumberSearch = async () => {
		await this.validateFormAndFind();
	};

	@action.bound
	onValueChange = async e => {
		if (e.key === 'Enter') {
			await this.validateFormAndFind();
		}
	};

	@action.bound
	onCheck = async (event, path) => {
		this.form.$(path).value = event.currentTarget.checked;
		await this.validateFormAndFind();
	};

	@action.bound
	onCheckDiscrepancies = async (event, path) => {
		this.form.$('showOnlyUndercharged').value = false;
		this.form.$('showOnlyOvercharged').value = false;
		this.form.$(path).value = event.currentTarget.checked;
		await this.validateFormAndFind();
	};

	@action.bound
	onOverchargeClick = async () => {
		this.form.$('showOnlyOvercharged').value = !this.form.$('showOnlyOvercharged').value;
		this.form.$('showOnlyUndercharged').value = false;
		this.form.$('showOnlyDiscrepancies').value = false;
		await this.validateFormAndFind();
	};

	@action.bound
	onUnderchargeClick = async () => {
		this.form.$('showOnlyUndercharged').value = !this.form.$('showOnlyUndercharged').value;
		this.form.$('showOnlyOvercharged').value = false;
		this.form.$('showOnlyDiscrepancies').value = false;
		await this.validateFormAndFind();
	};

	@action.bound
	onChargeTypeRemove = async () => {
		this.chargeTypeFilter = null;
		await this.validateFormAndFind();
	};

	@action.bound
	onExport = async () => {
		let notificationId = null;
		try {
			const costListViewStore = this.rootStore.shipmentCostViewStore.shipmentCostListViewStore;
			await this.validateFormAndFind(false);
			if (this.isFormValid && costListViewStore) {
				if (costListViewStore.lastSearchFilter != null) {
					let chargeDataArray = [];
					const chargeData = this.rootStore.shipmentCostViewStore.shipmentCostChargeListViewStore.data;
					if (chargeData != null && chargeData.length > 0) {
						notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.EXPORTDATA'));
						this.isExportDisabled = true;
						_.each(chargeData, charge => {
							let row = {};
							row['ChargeId'] = charge.name;
							row['QuotedRateTotal'] = charge.totalQuoted;
							row['BilledRateTotal'] = charge.totalBilled;
							row['DifferenceTotal'] = charge.totalDifference;
							chargeDataArray.push(row);
						});

						const xlsxResponse = await this.shipmentCostStore.exportToCsv({
							timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
							...costListViewStore.lastSearchFilter,
							chargeData: chargeDataArray,
						});

						if (xlsxResponse) {
							const base64String =
								'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' + xlsxResponse.data;
							urlToFile(
								base64String,
								'All_Shipment_Cost_Pages.xlsx',
								'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
							).then(file => {
								saveAs(file);
							});
						}
						this.rootStore.notificationStore.success('EXPORTTOXLS.SUCCESS');
					} else {
						this.rootStore.notificationStore.error(['EXPORTCSV.NOTHINGTOEXPORT']);
					}
				} else {
					this.rootStore.notificationStore.error(['SHIPMENTCOST.LASTSEARCHMISSING']);
				}
			}
		} catch (err) {
			this.rootStore.notificationStore.error(['EXPORTTOXLS.ERROR']);
		}
		this.isExportDisabled = false;
		if (notificationId) {
			this.rootStore.notificationStore.dismiss(notificationId);
		}
	};

	@action.bound
	downloadTemplate = downloadMissingBillData => {
		const workbook = new excelJS.Workbook();
		workbook.created = new Date();
		workbook.modified = new Date();
		const sheet = workbook.addWorksheet('ImportBillingData');
		sheet.columns = [
			{ header: 'TrackingNumber', key: 'trackingNumber' },
			{ header: 'InvoiceNumber', key: 'invoiceNumber' },
			{ header: 'InvoiceDate', key: 'invoiceDate' },
			{ header: 'ChargeId', key: 'chargeId' },
			{ header: 'BilledRate', key: 'billedRate' },
		];

		var requiredFieldStyle = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFC7CE' },
		};
		sheet.getCell('A1').fill = requiredFieldStyle;
		sheet.getCell('D1').fill = requiredFieldStyle;
		sheet.getCell('E1').fill = requiredFieldStyle;

		if (downloadMissingBillData) {
			_.each(this.shipmentCostTotals.missingTrackingNumbers, trackingNumber => {
				sheet.addRow({ trackingNumber: trackingNumber });
			});
		}

		workbook.xlsx.writeBuffer().then(function(buffer) {
			const blob = new Blob([buffer], { type: 'applicationi/xlsx' });
			saveAs(blob, downloadMissingBillData ? 'ImportMissingBillingData.xlsx' : 'ImportBillingData.xlsx');
		});
	};

	@action.bound async onFilesDrop(filesMeta) {
		if (filesMeta && filesMeta.length == 1) {
			let fileMeta = filesMeta[0];
			let reader = new FileReader();
			let xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
			let xls = 'application/vnd.ms-excel';
			if (
				fileMeta.type === xlsx ||
				(fileMeta.type === xls && !fileMeta.name.includes('csv', fileMeta.name.length - 3))
			) {
				// Excel processing (excluding csv)
				reader.onload = async evt => {
					const workbook = XLSX.read(evt.target.result, { type: 'binary' });
					const firstWorksheet = workbook.Sheets[workbook.SheetNames[0]];
					let templateFormat = this.getTemplateFormat(firstWorksheet);
					const xlsxData = XLSX.utils.sheet_to_json(firstWorksheet);

					if (xlsxData.length > 0) {
						this.uploadData = await this.mapBillingData(xlsxData, templateFormat);

						if (this.uploadData != null && this.uploadData.length > 0) {
							const trackingNumbersDistinct = _.uniq(
								_.map(this.uploadData, item => {
									return item.trackingNumber;
								})
							);
							this.trackingNumbersResult = await this.shipmentCostStore.checkTrackingNumbers(trackingNumbersDistinct);
							this.openImportBillingModal();
						}
					} else {
						this.loading = false;
						this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.NOVALIDDATAFORIMPORTFOUND']);
					}
				};
				reader.readAsBinaryString(fileMeta);
			} else {
				this.loading = false;
				this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.BILLINGIMPORTERBADFORMAT']);
			}
		} else if (filesMeta && filesMeta.length == 0) {
			this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.BILLINGIMPORTERROR']);
		} else {
			this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.BILLINGIMPORTERROR']);
		}
	}

	@action.bound
	getTemplateFormat = firstWorksheet => {
		if (
			(firstWorksheet['A1'] && firstWorksheet['A1'].v ? firstWorksheet['A1'].v.includes('TrackingNumber') : false) &&
			(firstWorksheet['B1'] && firstWorksheet['B1'].v ? firstWorksheet['B1'].v.includes('InvoiceNumber') : false)
		) {
			return null; // Scanship default template
		} else if (
			(firstWorksheet['D1'] && firstWorksheet['D1'].v ? firstWorksheet['D1'].v.includes('System') : false) &&
			(firstWorksheet['E1'] && firstWorksheet['E1'].v ? firstWorksheet['E1'].v.includes('Service Code') : false)
		) {
			return 'ups';
		} else if (
			(firstWorksheet['A1'] && firstWorksheet['A1'].v
				? firstWorksheet['A1'].v.includes('Consolidated Account')
				: false) &&
			(firstWorksheet['B1'] && firstWorksheet['B1'].v
				? firstWorksheet['B1'].v.includes('Bill to Account Number')
				: false)
		) {
			return 'fedex';
		}

		return null;
	};

	@action.bound
	mapBillingData = async (items, templateFormat) => {
		try {
			if (items.length > 0) {
				let decimalLength = '';
				let minValue = '';
				let emptyField = '';
				let missingRequiredField = false;
				let trackingNumbers = [];
				if (templateFormat == 'ups') {
					trackingNumbers = this.mapUpsTemplate(items);
				} else if (templateFormat == 'fedex') {
					trackingNumbers = this.mapFedexTemplate(items);
				} else {
					// If template is not found, fallback to default Scanship import template
					for (let item of items) {
						if (
							item.hasOwnProperty('TrackingNumber') &&
							item.hasOwnProperty('ChargeId') &&
							item.hasOwnProperty('BilledRate')
						) {
							if (item.TrackingNumber == undefined) {
								emptyField = 'TrackingNumber';
								break;
							}

							if (item.ChargeId == undefined) {
								emptyField = 'chargeId';
								break;
							}

							if (item.BilledRate == undefined || !validateNumberField(item.BilledRate)) {
								emptyField = 'BilledRate';
								break;
							} else if (!validateMinValue(item.BilledRate)) {
								minValue = 'BilledRate';
								break;
							} else if (!validateDecimalLengthField(item.BilledRate)) {
								decimalLength = 'BilledRate';
								break;
							}

							let invoiceDate = null;
							if (item.InvoiceDate) {
								let date = excelDateToJSDate(item.InvoiceDate);
								invoiceDate = moment(date)
									.utc(true)
									.format();
								if (invoiceDate == 'Invalid date') {
									invoiceDate = null;
								}
							}

							trackingNumbers.push({
								trackingNumber:
									typeof item.TrackingNumber === 'string' || item.TrackingNumber instanceof String
										? item.TrackingNumber.trim()
										: item.TrackingNumber,
								invoiceNumber: item.InvoiceNumber,
								invoiceDate: invoiceDate,
								chargeId: item.ChargeId.toString().trim(),
								billedRate: item.BilledRate,
							});
						} else {
							missingRequiredField = true;
							break;
						}
					}
				}

				if (emptyField !== '') {
					this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.EMPTYFIELD', { field: emptyField }]);
				} else if (missingRequiredField) {
					this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.REQUIREDCOLUMNMISSING']);
				} else if (minValue !== '') {
					this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.MINVALUE', { field: minValue }]);
				} else if (decimalLength !== '') {
					this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.DECIMALLENGTH', { field: decimalLength }]);
				} else {
					return trackingNumbers;
				}
			} else {
				this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.REQUIREDCOLUMNMISSING']);
			}
		} catch (err) {
			if (err.statusCode === 404 && err.data) {
				this.rootStore.notificationStore.error([
					'SHIPMENTCOST.IMPORTXLS.TNNOTFOUNDERROR',
					{ trackingNumber: err.data },
				]);
			} else {
				this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.BILLINGIMPORTERROR']);
			}
		}

		return null;
	};

	importBillingModal = new ModalParams();

	openImportBillingModal = () => {
		this.importBillingModal.open();
	};

	closeImportBillingModal = () => {
		this.uploadData = [];
		this.importBillingModal.close();
	};

	mapFedexTemplate(items) {
		let trackingNumbers = [];
		for (let item of items) {
			let invoiceDate = null;
			if (item['Invoice Date']) {
				invoiceDate = moment(item['Invoice Date'], 'YYYYMMDD')
					.utc(true)
					.format();
			}

			const extractedCharges = [];
			let shippingCostWithDiscounts = parseFloat(item['Transportation Charge Amount']);
			if (isNaN(shippingCostWithDiscounts)) {
				shippingCostWithDiscounts = 0.0;
			}

			for (const key in item) {
				if (key.startsWith('Tracking ID Charge Description')) {
					if (item[key] && item[key] !== '') {
						let chargeAmount = parseFloat(item[key.replace('Description', 'Amount')]);
						if (isNaN(chargeAmount)) {
							continue;
						} else {
							if (item[key] == 'Grace Discount' || item[key] == 'Performance Pricing') {
								shippingCostWithDiscounts += chargeAmount;
							} else {
								extractedCharges.push({
									chargeId: item[key],
									amount: chargeAmount,
								});
							}
						}
					}
				}
			}

			if (shippingCostWithDiscounts != 0.0) {
				extractedCharges.push({
					chargeId: 'Shipping',
					amount: shippingCostWithDiscounts.toFixed(2),
				});
			}

			for (let i = 0; i < extractedCharges.length; i++) {
				trackingNumbers.push({
					trackingNumber: item['Express or Ground Tracking ID'],
					invoiceNumber: item['Invoice Number'],
					invoiceDate: invoiceDate,
					chargeId: extractedCharges[i].chargeId,
					billedRate: extractedCharges[i].amount,
				});
			}
		}
		return trackingNumbers;
	}

	mapUpsTemplate(items) {
		let trackingNumbers = [];
		for (let item of items) {
			if (item['Net Charge'] == 0) {
				continue;
			}

			trackingNumbers.push({
				trackingNumber: item['Tracking Number'].trim(),
				chargeId: item['System'].trim(),
				billedRate: item['Net Charge'],
			});
		}
		return trackingNumbers;
	}

	@action.bound
	async uploadBillingData(isOverride) {
		if (
			this.trackingNumbersResult &&
			(this.trackingNumbersResult.newCharges == 0 && this.trackingNumbersResult.existingCharges == 0)
		) {
			this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.TNNOTFOUNDERROR']);
			this.closeImportBillingModal();
			return;
		}

		const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.IMPORTBILLINGDATA'));
		this.importBillingModal.close();
		try {
			await this.shipmentCostStore.uploadBillData(this.uploadData, isOverride, sourceOfChange.createBillDataFromXLS);
			this.uploadData = [];
			this.rootStore.notificationStore.success(['SHIPMENTCOST.IMPORTXLS.BILLINGIMPORT']);
		} catch (err) {
			if (err.statusCode === 404) {
				this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.TNNOTFOUNDERROR']);
			} else {
				this.rootStore.notificationStore.error(['SHIPMENTCOST.IMPORTXLS.BILLINGIMPORTERROR']);
			}
		}
		this.rootStore.notificationStore.dismiss(notificationId);
	}
}

export default ShipmentCostViewStore;



// WEBPACK FOOTER //
// ./src/modules/shipment-cost/stores/ShipmentCostViewStore.js